import { useEffect, useState, useCallback } from "react";
import { useEstablishments } from "@/context/EstablishmentContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Star, ThumbsUp, AlertCircle, MessageSquareReply, Gauge, Info } from "lucide-react";

function Unavailable({ label }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center" data-testid="data-unavailable">
      <Info className="w-8 h-8 text-slate-300 mx-auto mb-3" />
      <p className="text-slate-500">Donnée indisponible pour {label}.</p>
      <p className="text-slate-400 text-sm mt-1">La connexion aux avis Google sera disponible dans une prochaine étape (nécessite l'API Google Business Profile).</p>
    </div>
  );
}

export default function Reputation() {
  const { current } = useEstablishments();
  const [rev, setRev] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!current) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/establishments/${current.id}/reviews`);
      setRev(data && data.avg_rating != null ? data : null);
    } finally { setLoading(false); }
  }, [current]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-8 grid place-items-center min-h-[60vh]"><Gauge className="w-8 h-8 text-blue-600 animate-spin" /></div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Réputation & avis</h1>
      <p className="text-slate-500 mt-1">Ce que vos clients disent de vous.</p>

      {!rev ? <div className="mt-6"><Unavailable label="cet établissement" /></div> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            {[
              { label: "Note moyenne", value: `${rev.avg_rating}/5`, icon: Star, color: "text-amber-500" },
              { label: "Nombre d'avis", value: rev.count, icon: MessageSquareReply, color: "text-blue-500" },
              { label: "Avis positifs", value: rev.positive_count, icon: ThumbsUp, color: "text-emerald-500" },
              { label: "Taux de réponse", value: `${rev.response_rate}%`, icon: AlertCircle, color: "text-indigo-500" },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-2xl p-5 border border-slate-200" data-testid={`rep-stat-${i}`}>
                <s.icon className={`w-5 h-5 ${s.color}`} />
                <div className="font-heading text-2xl font-extrabold mt-2">{s.value}</div>
                <div className="text-xs text-slate-500 mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-6 mt-6">
            <div className="bg-white rounded-2xl p-6 border border-slate-200">
              <h2 className="font-heading font-semibold mb-4">Thèmes récurrents</h2>
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600 mb-2">Points forts</p>
              <div className="flex flex-wrap gap-2">
                {rev.themes?.positive?.map((t, i) => (
                  <span key={i} className="text-sm bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full">{t.theme} · {t.count}</span>
                ))}
              </div>
              <p className="text-xs font-semibold uppercase tracking-wide text-orange-600 mt-4 mb-2">À surveiller</p>
              <div className="flex flex-wrap gap-2">
                {rev.themes?.watch?.map((t, i) => (
                  <span key={i} className="text-sm bg-orange-50 text-orange-700 px-3 py-1 rounded-full">{t.theme} · {t.count}</span>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-4">Analyse par IA des thèmes — disponible avec la connexion aux avis (étape suivante).</p>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-slate-200">
              <h2 className="font-heading font-semibold mb-4">Avis récents</h2>
              <div className="space-y-4">
                {rev.recent?.map((r, i) => (
                  <div key={i} className="border-b border-slate-100 pb-4 last:border-0" data-testid={`review-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm text-slate-800">{r.author}</span>
                      <span className="flex gap-0.5">{[...Array(5)].map((_, j) => <Star key={j} className={`w-3.5 h-3.5 ${j < r.rating ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />)}</span>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">{r.text}</p>
                    <Button size="sm" variant="outline" onClick={() => toast.info("Génération de réponse IA — disponible à l'étape Assistant IA")} data-testid={`reply-review-${i}`} className="mt-2 h-7 text-xs rounded-lg">
                      <MessageSquareReply className="w-3.5 h-3.5 mr-1" /> Générer une réponse
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
